import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import classnames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import styled from 'styled-components';

import { Position } from '__generated__/globalTypes';
import { Button } from 'atoms/buttons/Button';
import { FontAwesomeIcon } from 'atoms/icons';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { HeadlineL, HeadlineM, LabelL, LabelM, LabelS } from 'atoms/typography';
import Dialog from 'components/dialog';
import useStack from 'hooks/useStack';

const Root = styled.div`
  padding: var(--double-unit);
`;
const Appearances = styled(Vertical)`
  padding: var(--double-unit);
  text-align: center;
`;
const AppearanceContainer = styled.div`
  width: 20%;
  color: var(--c-neutral-500);
  &.selected {
    color: var(--c-neutral-1000);
  }
`;
const Substituted = styled(LabelM)`
  text-decoration: line-through;
`;

type Appearance = {
  player?: {
    displayName: string;
  };
  team?: {
    name: string;
  } | null;
  season?: {
    startYear: number;
  } | null;
  bonus?: number | null;
  positions: Position[];
};

type AppearanceSlot = Appearance & {
  label: string;
};

const formatAppearance = (appearance: Appearance | null | undefined) =>
  appearance?.player
    ? `${[
        appearance.player.displayName,
        appearance.team?.name,
        appearance.season?.startYear,
      ]
        .filter(Boolean)
        .join(' - ')} (${appearance.bonus})`
    : '';

const Appearance = ({
  appearance,
  proposedAppearance,
}: {
  appearance: Appearance | null | undefined;
  proposedAppearance: Appearance | null | undefined;
}) => {
  return (
    <LabelL as="p">
      {proposedAppearance ? (
        <>
          <LabelM>{formatAppearance(proposedAppearance)}</LabelM>
          <Substituted>{formatAppearance(appearance)}</Substituted>
        </>
      ) : (
        <LabelM>{formatAppearance(appearance)}</LabelM>
      )}
    </LabelL>
  );
};

const isLineupReady = (lineup: (Appearance | null)[]) =>
  lineup.every(appearance => !!appearance?.player);

const findEligibleSlotIndex = ({
  lineup,
  activeSlots,
  proposedAppearance,
  freeSlotOnly = false,
}: {
  lineup: Appearance[];
  activeSlots?: number[];
  proposedAppearance?: Appearance | null;
  freeSlotOnly?: boolean;
}): number => {
  if (!proposedAppearance || !activeSlots) return -1;

  const freeSlot = lineup.findIndex(
    (appearance, index) =>
      activeSlots?.includes(index) &&
      !!appearance.positions.find(
        position =>
          proposedAppearance.positions.includes(position) &&
          (!freeSlotOnly || !appearance.player)
      )
  );
  if (freeSlot === -1 && freeSlotOnly) {
    return findEligibleSlotIndex({
      lineup,
      activeSlots,
      proposedAppearance,
      freeSlotOnly: false,
    });
  }

  return freeSlot;
};

type Props = {
  requestAppearance: () => Promise<Appearance | null | undefined>;
  acceptAppearance: () => Promise<boolean>;
  resetAppearances: () => Promise<void>;
  onSubmit: () => void;
  onClose?: () => void;
  open: boolean;
  lineup: {
    positions: Position[];
    label: string;
  }[];
  slotRounds: number[][];
  disclaimers: MessageDescriptor[];
};

export const ComposeTeam = ({
  onSubmit,
  requestAppearance,
  acceptAppearance,
  resetAppearances,
  open,
  onClose,
  lineup: defaultLineup,
  slotRounds,
  disclaimers,
}: Props) => {
  const [initialized, setInitialized] = useState(false);
  const [reseting, setReseting] = useState(true);
  const [proposedAppearance, setProposedAppearance] =
    useState<Appearance | null>();
  const [lineup, setLineup] = useState<AppearanceSlot[]>(defaultLineup);
  const [activeSlots, nextRound, resetRound] = useStack<number[]>(slotRounds);
  const ready = isLineupReady(lineup);

  const requestNewAppearance = useCallback(async () => {
    setProposedAppearance(await requestAppearance());
  }, [requestAppearance]);

  const eligibleSlot = findEligibleSlotIndex({
    lineup,
    activeSlots,
    proposedAppearance,
  });

  const acceptProposedAppearance = async () => {
    if (eligibleSlot === -1) {
      await requestNewAppearance();
      return;
    }

    const success = await acceptAppearance();

    if (!success) return;

    if (eligibleSlot !== -1) {
      const newLineup = [...lineup];
      newLineup[eligibleSlot] = {
        ...newLineup[eligibleSlot],
        ...proposedAppearance,
      };
      setLineup(newLineup);
      setProposedAppearance(null);
      if (activeSlots?.every(slot => !!newLineup[slot].player)) {
        nextRound();
      }
      if (!isLineupReady(newLineup)) {
        requestNewAppearance();
      }
    }
  };

  const onReset = useCallback(async () => {
    setReseting(true);
    resetRound();
    setProposedAppearance(null);
    setLineup(defaultLineup);
    await resetAppearances();
    requestNewAppearance();
    setReseting(false);
  }, [defaultLineup, resetAppearances, requestNewAppearance, resetRound]);

  useEffect(() => {
    if (
      open &&
      !initialized &&
      !lineup.some(appearance => appearance?.player)
    ) {
      setInitialized(true);
      onReset();
    }
  }, [open, initialized, onReset, lineup]);

  if (reseting) {
    return null;
  }

  return (
    <Dialog open={open} maxWidth="md" fullWidth onClose={onClose}>
      <Root>
        <Vertical gap={4}>
          <Appearances gap={4}>
            <HeadlineL as="p">
              <FormattedMessage
                id="noCardEntry.ComposeTeam.title"
                defaultMessage="Validate your lineup"
              />
            </HeadlineL>
            <Horizontal>
              {lineup.map((appearance, index) => (
                <AppearanceContainer
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className={classnames({
                    selected: eligibleSlot === index,
                  })}
                >
                  <HeadlineM as="p">
                    {appearance.label}{' '}
                    {!!appearance.player && (
                      <FontAwesomeIcon icon={faCheck} size="xs" />
                    )}
                  </HeadlineM>
                  <Appearance
                    appearance={appearance}
                    proposedAppearance={
                      eligibleSlot === index ? proposedAppearance : null
                    }
                  />
                </AppearanceContainer>
              ))}
            </Horizontal>
          </Appearances>
          <Vertical gap={4}>
            <Vertical gap={2}>
              <Horizontal>
                <Button
                  fullWidth
                  size="medium"
                  color="tertiary"
                  onClick={requestNewAppearance}
                  disabled={ready}
                >
                  <FormattedMessage
                    id="noCardEntry.ComposeTeam.deny"
                    defaultMessage="Deny"
                  />
                </Button>
                <Button
                  fullWidth
                  size="medium"
                  color="primary"
                  onClick={acceptProposedAppearance}
                  disabled={ready}
                >
                  <FormattedMessage
                    id="noCardEntry.ComposeTeam.accept"
                    defaultMessage="Accept"
                  />
                </Button>
              </Horizontal>
              <Button
                fullWidth
                color="tertiary"
                size="medium"
                onClick={onReset}
              >
                <FormattedMessage
                  id="noCardEntry.ComposeTeam.reset"
                  defaultMessage="Reset"
                />
              </Button>
            </Vertical>
            <Button
              fullWidth
              size="medium"
              onClick={onSubmit}
              disabled={!ready}
            >
              <FormattedMessage
                id="noCardEntry.ComposeTeam.submit"
                defaultMessage="Submit"
              />
            </Button>
          </Vertical>
          <Vertical gap={1}>
            {disclaimers.map(disclaimer => (
              <LabelS key={disclaimer.id} color="var(--c-neutral-600)">
                <FormattedMessage {...disclaimer} />
              </LabelS>
            ))}
          </Vertical>
        </Vertical>
      </Root>
    </Dialog>
  );
};
